import apiClient from './api';
import type { ServiceProviderListItem } from './serviceProviderAccountService';

export interface ServiceBookingOrderSubItem {
  id: string;
  orderId: string;
  orderNo: string;
  serviceDate: string;
  serviceTimeDesc: string;
  petQuantity: number;
  unitPrice: number;
  totalAmount: number;
  payStatus: string;
  meituanOrderFulfillStatus: number;
  dispatchRemark: string;
  assignedStaffId: string;
  assignedStaffName: string;
  assignedStaffPhone: string;
  assignedStaffAvatar: string;
  assignedAt: string;
  serviceRecords: ServiceBookingServiceRecord[];
  proofMedia: string[];
  completedAt: string;
  status: string;
  statusText: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubOrderSettlementListItem {
  id: string;
  orderNo: string;
  subOrderNo: string;
  serviceProviderId: string;
  serviceProviderName: string;
  serviceProviderPhone: string;
  contactName: string;
  contactPhone: string;
  serviceAddress: string;
  serviceContent: string;
  flowType?: string;
  settlementAmount: number;
  status: string;
  serviceDate: string;
  completedAt: string;
  settledAt: string;
  eligibleSettleAt: string;
  remark?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubOrderSettlementListResponse {
  list: SubOrderSettlementListItem[];
  subOrderId: string;
  subOrderNo: string;
}

export interface UpdateSubOrderSettlementRequest {
  settlementId: string;
  settlementAmount: number;
  completedAt: string;
  eligibleSettleAt: string;
}

export interface CreateAfterSaleDeductionSettlementRequest {
  settlementAmount: number;
  remark?: string;
}

export interface ServiceBookingServiceRecord {
  title: string;
  desc: string;
  occurredAt: string;
  operator: string;
  remark?: string;
  images?: string[];
  processStepId?: string;
  processStepName?: string;
  processStepSort?: number;
  uploadRequirement?: string;
}

export interface ServiceBookingAddressSnapshot {
  contactName: string;
  contactPhone: string;
  provinceName: string;
  cityName: string;
  districtName: string;
  addressDetail: string;
  latitude: number;
  longitude: number;
  tag: string;
  remark: string;
  fullText: string;
}

export interface ServiceBookingOrder {
  id: string;
  orderNo: string;
  source: string;
  creationMode: string;
  createdByUserId: string;
  createdByUserName: string;
  externalOrderId: string;
  externalShopId: string;
  externalShopName: string;
  externalReservationNo: string;
  receiptCode?: string;
  couponInfo?: Array<{
    couponCode: string;
    verified: boolean;
    verifyStatus: number;
    amount: number;
  }>;
  acceptedByMerchant: number;
  meituanOrderFulfillStatus: number;
  userId: string;
  serviceProviderId: string;
  serviceProviderName: string;
  productId: string;
  productName: string;
  categoryId: string;
  categoryName: string;
  addressId: string;
  petId: string;
  petQuantity: number;
  contactName: string;
  contactPhone: string;
  addressText: string;
  addressSnapshot?: ServiceBookingAddressSnapshot;
  petName: string;
  serviceDates: string[];
  serviceDateStart: string;
  serviceDateEnd: string;
  serviceTimeDesc: string;
  totalDays: number;
  remark: string;
  unitPrice: number;
  totalAmount: number;
  payStatus: string;
  payMethod: string;
  paidAt: string;
  dispatchMode: string;
  dispatchRemark: string;
  assignedStaffId: string;
  assignedStaffName: string;
  assignedStaffPhone: string;
  assignedStaffAvatar: string;
  assignedAt: string;
  fulfillmentCompletedAt: string;
  meituanVerifiedAt: string;
  meituanVerifiedReceiptCode: string;
  meituanVerifiedAmount: number;
  meituanMerchantMarketingAmount: number;
  meituanAddPriceAmount: number;
  meituanAddPriceUrl: string;
  meituanAddPricePaid: number;
  meituanAddPriceSuccessTime: string;
  meituanAddPriceSerialNumber: string;
  meituanAddPriceThirdPartyPaymentInfo: string;
  status: string;
  statusText: string;
  items: ServiceBookingOrderSubItem[];
  createdAt: string;
  updatedAt: string;
}

export interface ServiceBookingOrderListParams {
  source?: string;
  creationMode?: string;
  createdByUserId?: string;
  page?: number;
  pageSize?: number;
  orderNo?: string;
  couponCode?: string;
  externalReservationNo?: string;
  externalOrderId?: string;
  contactName?: string;
  contactPhone?: string;
  address?: string;
  staffName?: string;
  staffPhone?: string;
  serviceDate?: string;
  createdAtStart?: string;
  createdAtEnd?: string;
  completedAtStart?: string;
  completedAtEnd?: string;
  status?: string;
  payStatus?: string;
  serviceProviderId?: string;
  serviceProviderName?: string;
}

export interface ServiceBookingOrderListResponse {
  list: ServiceBookingOrder[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateServiceBookingOrderRequest {
  source: string;
  productId: string;
  productName: string;
  categoryId: string;
  categoryName: string;
  contactName: string;
  contactPhone: string;
  provinceName?: string;
  cityName?: string;
  districtName?: string;
  addressDetail: string;
  latitude?: number;
  longitude?: number;
  petQuantity?: number;
  serviceStartAt: string;
  serviceEndAt?: string;
  totalAmount: number;
  unitPrice?: number;
  paidAt?: string;
  remark?: string;
  externalOrderId?: string;
  externalReservationNo?: string;
  externalShopId?: string;
}

export interface CorrectPaymentTimeRequest {
  paidAt: string;
  reason: string;
}

export interface CorrectBasicInfoRequest {
  contactName: string;
  contactPhone: string;
  provinceName?: string;
  cityName?: string;
  districtName?: string;
  addressDetail: string;
  serviceStartAt: string;
  serviceEndAt?: string;
  remark?: string;
  reason: string;
}

export interface PreviewMeituanOrderByReservationNoRequest {
  reservationNo: string;
  externalShopId: string;
}

export interface PreviewMeituanOrderByReservationNoResponse {
  traceId?: string;
  reservationNo?: string;
  externalShopId?: string;
  shopName?: string;
  businessId?: string;
  orderId?: string;
  unifiedOrderId?: string;
  amount?: string;
  paidAmount?: string;
  productId?: string;
  productName?: string;
  receiptCode?: string;
  receiptPaymentShares?: MeituanPaymentShareItem[];
  receiptList?: Array<Record<string, unknown>>;
  order?: Record<string, unknown>;
  productDTO?: Record<string, unknown>;
  extraPayments?: Array<Record<string, unknown>>;
  orderPreAmountList?: Array<Record<string, unknown>>;
  refundApplicationList?: Array<Record<string, unknown>>;
  techInfo?: Record<string, unknown>;
}

export interface UpdateMeituanOrderInfoRequest {
  externalOrderId: string;
  externalReservationNo: string;
  externalShopId: string;
}

export interface AssignableStaffSameTimeOrderItem {
  orderId: string;
  orderNo: string;
  orderItemId: string;
  orderItemNo: string;
  productName: string;
  serviceDate: string;
  serviceTimeDesc: string;
  status: string;
  statusText: string;
}

export interface AssignableStaffDateItem {
  orderItemId: string;
  orderItemNo: string;
  serviceDate: string;
  serviceTimeDesc: string;
  label: string;
  hasTimeConflict: boolean;
}

export interface AssignableStaffListItem extends ServiceProviderListItem {
  orderMode: boolean;
  autoAcceptOrder: boolean;
  assignable: boolean;
  assignableItemCount: number;
  assignableDates: string[];
  assignableDateItems: AssignableStaffDateItem[];
  categoryMatched: boolean;
  serviceTimeMatched: boolean;
  hasTimeConflict: boolean;
  dispatchDistanceMatched: boolean;
  maxDispatchDistanceKm: number;
  hasRejectedCurrentOrder: boolean;
  hasTimedOutCurrentOrder: boolean;
  sameTimeOrderCount: number;
  sameTimeOrders: AssignableStaffSameTimeOrderItem[];
}

export interface AssignableStaffListResponse {
  list: AssignableStaffListItem[];
  total: number;
  page: number;
  pageSize: number;
  onlyAssignable?: boolean;
}

export interface AssignableStaffListParams {
  orderItemId?: string;
  page?: number;
  pageSize?: number;
  onlyAssignable?: boolean;
  address?: string;
  staffName?: string;
  staffPhone?: string;
  requireCertification?: boolean;
  requireOrderMode?: boolean;
  requireServiceCategory?: boolean;
  requireServiceTime?: boolean;
  requireDispatchDistance?: boolean;
  excludeTimeConflict?: boolean;
  excludeRejectedCurrentOrder?: boolean;
  excludeTimedOutCurrentOrder?: boolean;
}

export interface AssignServiceBookingOrderRequest {
  assignedStaffId: string;
  assignReason?: string;
  orderItemId?: string;
  orderItemIds?: string[];
  noNeedStaffAccept?: boolean;
}

export interface UnassignServiceBookingOrderRequest {
  cancelReason?: string;
}

export interface CancelServiceBookingOrderRequest {
  cancelReason?: string;
}

export interface RejectMeituanOrderRequest {
  rejectReason?: string;
}

export interface MerchantCancelMeituanOrderRequest {
  cancelReason: string;
  type: number;
}

export interface MerchantCancelMeituanOrderResponse {
  order: ServiceBookingOrder;
  meituan?: unknown;
  traceId?: string;
  orderId?: string;
  tradeType?: number;
}

export interface MerchantModifyMeituanOrderRequest {
  reserveUserName: string;
  reservePhone: string;
  reserveAddress: string;
  provinceName?: string;
  cityName?: string;
  districtName?: string;
  addressDetail?: string;
  bookStartTime: number;
  bookEndTime: number;
  deliveryUserName?: string;
  deliveryAddress?: string;
  deliveryPhone?: string;
}

export interface MerchantModifyMeituanOrderResponse {
  order: ServiceBookingOrder;
  meituan?: unknown;
  traceId?: string;
  result?: boolean;
  reserveOrderId?: string;
}

export interface MeituanAddPriceItemRequest {
  id?: string;
  unitPrice: number;
  size: number;
  operateType?: number;
}

export interface MeituanBalancePaymentItemRequest {
  id?: string;
  unitPrice: number;
  type: number;
  size: number;
}

export interface AddPriceMeituanOrderRequest {
  operateType?: number;
  addPriceDetails?: MeituanAddPriceItemRequest[];
  balancePaymentDetails?: MeituanBalancePaymentItemRequest[];
}

export interface AddPriceMeituanOrderResponse {
  order: ServiceBookingOrder;
  meituan?: unknown;
  traceId?: string;
  reserveOrderId?: string;
  addPriceUrl?: string;
  addPriceAmount?: number;
  operateType?: number;
}

export interface MeituanReservationChangeRecord {
  id: string;
  callbackId: string;
  messageType: string;
  meituanOrderId: string;
  serviceOrderId: string;
  serviceOrderNo: string;
  productId: string;
  productName: string;
  reserveBeginTime: string;
  reserveEndTime: string;
  userAddress: string;
  userName: string;
  remarks: string;
  reserveMobile: string;
  reservePhone: string;
  addressLatitude: string;
  addressLongitude: string;
  autoConfirmChange: number;
  processStatus: number;
  processMessage: string;
  createdAt: string;
  updatedAt: string;
}

export interface MeituanPaymentShareItem {
  receiptCode: string;
  amount: number;
  amountType: number;
  platformAmount?: string;
  merchantAmount?: string;
}

export interface MeituanPaymentSharesResponse {
  orderId: string;
  serviceOrderId: string;
  serviceOrderNo: string;
  traceId: string;
  result: MeituanPaymentShareItem[];
}

export interface MeituanOrderQueryInfoReceipt {
  receiptCode?: string;
  status?: number;
  receiptStatus?: number;
  serialNumber?: string;
  receiptBeginTime?: number;
  receiptEndTime?: number;
  amountShareList?: Array<Record<string, unknown>>;
  relatedBook?: boolean;
}

export interface MeituanOrderQueryInfoExtraPayment {
  paymentType?: number;
  amount?: string;
  payStatus?: number;
  receiptStatus?: number;
  serialNumber?: string;
}

export interface MeituanOrderQueryInfoResponse {
  serviceOrderId: string;
  serviceOrderNo: string;
  orderId: string;
  unifiedOrderId: string;
  traceId: string;
  amount?: string;
  receipts?: MeituanOrderQueryInfoReceipt[];
  extraPayments?: MeituanOrderQueryInfoExtraPayment[];
  order?: Record<string, unknown>;
  receiptList?: MeituanOrderQueryInfoReceipt[];
  orderPreAmountList?: Array<Record<string, unknown>>;
  productDTO?: Record<string, unknown>;
  refundApplicationList?: Array<Record<string, unknown>>;
  techInfo?: Record<string, unknown>;
}

export interface MeituanShopReviewScore {
  title: string;
  score: number;
  accurateScore: number;
}

export interface MeituanShopReviewTrade {
  title: string;
  content: string;
}

export interface MeituanShopReviewItem {
  reviewId: string;
  star: number;
  accurateStar: number;
  reviewTime: string;
  consumeOrderId: string;
  consumeAmount: string;
  consumeTime: string;
  serialNumbers: string;
  reviewQuality: number;
  scoreDetails: MeituanShopReviewScore[];
  tradeDetails: MeituanShopReviewTrade[];
}

export interface MeituanShopReviewListParams {
  star?: number;
  offset?: number;
  limit?: number;
  beginTime: string;
  endTime: string;
  platform?: number;
}

export interface MeituanShopReviewListResponse {
  serviceOrderId: string;
  serviceOrderNo: string;
  traceId: string;
  star: number;
  offset: number;
  limit: number;
  platform: number;
  list: MeituanShopReviewItem[];
}

export const serviceBookingOrderService = {
  create: async (data: CreateServiceBookingOrderRequest): Promise<ServiceBookingOrder> => {
    const response = await apiClient.post<ServiceBookingOrder>('/service-booking-orders', data);
    return response.data;
  },
  previewMeituanOrderByReservationNo: async (
    data: PreviewMeituanOrderByReservationNoRequest,
  ): Promise<PreviewMeituanOrderByReservationNoResponse> => {
    const response = await apiClient.post<PreviewMeituanOrderByReservationNoResponse>(
      '/service-booking-orders/meituan/preview-by-reservation-no',
      data,
    );
    return response.data;
  },
  list: async (params?: ServiceBookingOrderListParams): Promise<ServiceBookingOrderListResponse> => {
    const response = await apiClient.get<ServiceBookingOrderListResponse>('/service-booking-orders', { params });
    return response.data;
  },
  get: async (id: string): Promise<ServiceBookingOrder> => {
    const response = await apiClient.get<ServiceBookingOrder>(`/service-booking-orders/${id}`);
    return response.data;
  },
  correctPaymentTime: async (id: string, data: CorrectPaymentTimeRequest): Promise<ServiceBookingOrder> => {
    const response = await apiClient.put<ServiceBookingOrder>(`/service-booking-orders/${id}/payment-time`, data);
    return response.data;
  },
  correctBasicInfo: async (id: string, data: CorrectBasicInfoRequest): Promise<ServiceBookingOrder> => {
    const response = await apiClient.put<ServiceBookingOrder>(`/service-booking-orders/${id}/basic-info`, data);
    return response.data;
  },
  listSubOrderSettlements: async (subOrderId: string): Promise<SubOrderSettlementListResponse> => {
    const response = await apiClient.get<SubOrderSettlementListResponse>(`/service-booking-orders/sub-orders/${subOrderId}/settlements`);
    return response.data;
  },
  generateSubOrderSettlement: async (subOrderId: string): Promise<{ subOrderId: string }> => {
    const response = await apiClient.post<{ subOrderId: string }>(`/service-booking-orders/sub-orders/${subOrderId}/settlements/generate`);
    return response.data;
  },
  updateSubOrderSettlement: async (
    subOrderId: string,
    data: UpdateSubOrderSettlementRequest,
  ): Promise<{ id: string; subOrderId: string; settlementAmount: number; completedAt: string; eligibleSettleAt: string }> => {
    const response = await apiClient.put<{ id: string; subOrderId: string; settlementAmount: number; completedAt: string; eligibleSettleAt: string }>(
      `/service-booking-orders/sub-orders/${subOrderId}/settlements`,
      data,
    );
    return response.data;
  },
  settleSubOrderSettlement: async (subOrderId: string): Promise<{ settlementId: string; settled: boolean; amount: number }> => {
    const response = await apiClient.post<{ settlementId: string; settled: boolean; amount: number }>(
      `/service-booking-orders/sub-orders/${subOrderId}/settlements/settle`,
    );
    return response.data;
  },
  settleSubOrderSettlementById: async (
    subOrderId: string,
    settlementId: string,
  ): Promise<{ settlementId: string; settled: boolean; amount: number }> => {
    const response = await apiClient.post<{ settlementId: string; settled: boolean; amount: number }>(
      `/service-booking-orders/sub-orders/${subOrderId}/settlements/settle`,
      null,
      { params: { settlementId } },
    );
    return response.data;
  },
  createAfterSaleDeductionSettlement: async (
    subOrderId: string,
    data: CreateAfterSaleDeductionSettlementRequest,
  ): Promise<{ id: string; subOrderId: string; settlementAmount: number; status: string; flowType: string }> => {
    const response = await apiClient.post<{ id: string; subOrderId: string; settlementAmount: number; status: string; flowType: string }>(
      `/service-booking-orders/sub-orders/${subOrderId}/settlements/after-sale-deduction`,
      data,
    );
    return response.data;
  },
  listAssignableStaff: async (id: string, params?: AssignableStaffListParams): Promise<AssignableStaffListResponse> => {
    const response = await apiClient.get<AssignableStaffListResponse>(`/service-booking-orders/${id}/assignable-staff`, { params });
    return response.data;
  },
  assign: async (id: string, data: AssignServiceBookingOrderRequest): Promise<ServiceBookingOrder> => {
    const response = await apiClient.put<ServiceBookingOrder>(`/service-booking-orders/${id}/assign`, data);
    return response.data;
  },
  unassign: async (id: string, data?: UnassignServiceBookingOrderRequest): Promise<ServiceBookingOrder> => {
    const response = await apiClient.put<ServiceBookingOrder>(`/service-booking-orders/${id}/unassign`, data || {});
    return response.data;
  },
  cancel: async (id: string, data?: CancelServiceBookingOrderRequest): Promise<ServiceBookingOrder> => {
    const response = await apiClient.put<ServiceBookingOrder>(`/service-booking-orders/${id}/cancel`, data || {});
    return response.data;
  },
  confirmMeituanOrder: async (id: string): Promise<ServiceBookingOrder> => {
    const response = await apiClient.post<ServiceBookingOrder>(`/service-booking-orders/${id}/meituan-confirm`);
    return response.data;
  },
  rejectMeituanOrder: async (id: string, data?: RejectMeituanOrderRequest): Promise<ServiceBookingOrder> => {
    const response = await apiClient.post<ServiceBookingOrder>(`/service-booking-orders/${id}/meituan-reject`, data || {});
    return response.data;
  },
  merchantCancelMeituanOrder: async (id: string, data: MerchantCancelMeituanOrderRequest): Promise<MerchantCancelMeituanOrderResponse> => {
    const response = await apiClient.post<MerchantCancelMeituanOrderResponse>(`/service-booking-orders/${id}/meituan/merchant-cancel`, data);
    return response.data;
  },
  merchantModifyMeituanOrder: async (id: string, data: MerchantModifyMeituanOrderRequest): Promise<MerchantModifyMeituanOrderResponse> => {
    const response = await apiClient.post<MerchantModifyMeituanOrderResponse>(`/service-booking-orders/${id}/meituan/merchant-modify`, data);
    return response.data;
  },
  addPriceMeituanOrder: async (id: string, data: AddPriceMeituanOrderRequest): Promise<AddPriceMeituanOrderResponse> => {
    const response = await apiClient.post<AddPriceMeituanOrderResponse>(`/service-booking-orders/${id}/meituan/add-price`, data);
    return response.data;
  },
  updateMeituanOrderInfo: async (id: string, data: UpdateMeituanOrderInfoRequest): Promise<ServiceBookingOrder> => {
    const response = await apiClient.put<ServiceBookingOrder>(`/service-booking-orders/${id}/meituan/order-info`, data);
    return response.data;
  },
  getLatestMeituanChange: async (id: string): Promise<MeituanReservationChangeRecord | null> => {
    const response = await apiClient.get<MeituanReservationChangeRecord | null>(`/service-booking-orders/${id}/meituan-change/latest`);
    return response.data;
  },
  queryMeituanPaymentShares: async (id: string): Promise<MeituanPaymentSharesResponse> => {
    const response = await apiClient.get<MeituanPaymentSharesResponse>(`/service-booking-orders/${id}/meituan/payment-shares`);
    return response.data;
  },
  queryMeituanOrderInfo: async (id: string): Promise<MeituanOrderQueryInfoResponse> => {
    const response = await apiClient.get<MeituanOrderQueryInfoResponse>(`/service-booking-orders/${id}/meituan/order-query-info`);
    return response.data;
  },
  queryMeituanShopReviews: async (id: string, params: MeituanShopReviewListParams): Promise<MeituanShopReviewListResponse> => {
    const response = await apiClient.get<MeituanShopReviewListResponse>(`/service-booking-orders/${id}/meituan/shop-reviews`, { params });
    return response.data;
  },
  confirmMeituanChange: async (id: string): Promise<ServiceBookingOrder> => {
    const response = await apiClient.post<ServiceBookingOrder>(`/service-booking-orders/${id}/meituan-change/confirm`);
    return response.data;
  },
  rejectMeituanChange: async (id: string): Promise<ServiceBookingOrder> => {
    const response = await apiClient.post<ServiceBookingOrder>(`/service-booking-orders/${id}/meituan-change/reject`);
    return response.data;
  },
};


